import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase-config";

// Only councils that have data in the processed dataset
const COUNCILS = [
  "Barking and Dagenham",
  "Bexley",
  "Brent",
  "Bromley",
  "Camden",
  "City of London",
  "Croydon",
  "Greenwich",
  "Hackney",
  "Hammersmith and Fulham",
  "Haringey",
  "Havering",
  "Hillingdon",
  "Islington",
  "Kensington and Chelsea",
  "Lewisham",
  "Newham",
  "Richmond upon Thames",
  "Southwark",
  "Tower Hamlets",
  "Westminster",
];

function CouncilRecommendation({ onClose, inline = false }) {
  useEffect(() => {
    if (inline) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, inline]);

  const [selectedCouncil, setSelectedCouncil] = useState("");
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [error, setError] = useState("");

  const getRecommendation = async () => {
    if (!selectedCouncil) return;

    setLoading(true);
    setError("");
    setRecommendation(null);

    try {
      const snap = await getDocs(collection(db, "library_councils"));
      const councilDoc = snap.docs.find(
        (d) => d.data().name === selectedCouncil,
      );

      if (!councilDoc) {
        setError(`No data available for ${selectedCouncil} yet.`);
        return;
      }

      const { recommended_books } = councilDoc.data();
      if (!recommended_books || recommended_books.length === 0) {
        setError("No rated books available for this council.");
        return;
      }

      const random =
        recommended_books[Math.floor(Math.random() * recommended_books.length)];
      setRecommendation(random);
    } catch (err) {
      console.error(err);
      setError("Failed to load recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inner = (
    <>
      <div className="dialog-header">
        <div className="dialog-icon">🎲</div>
        <div>
          <h2 className="dialog-title">Book Recommendation</h2>
          <p className="dialog-council">
            From your council's unloaned collection
          </p>
        </div>
      </div>

      <div className="council-picker">
        <select
          id="council-select"
          value={selectedCouncil}
          onChange={(e) => setSelectedCouncil(e.target.value)}
          className="council-select"
        >
          <option value="">— Choose a council —</option>
          {COUNCILS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {!recommendation && (
          <button
            className="recommend-btn"
            onClick={getRecommendation}
            disabled={!selectedCouncil || loading}
          >
            {loading ? "Finding a book…" : "Get a Random Book"}
          </button>
        )}
      </div>

      {error && <p className="error-message">{error}</p>}

      {recommendation && (
        <div className="recommendation-result">
          <div className="recommendation-book">
            <div className="rec-cover">📖</div>
            <div className="rec-details">
              <h3 className="rec-title">
                {recommendation.isbn13 ? (
                  <a
                    href={`https://openlibrary.org/isbn/${recommendation.isbn13}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {recommendation.title}
                  </a>
                ) : (
                  recommendation.title
                )}
              </h3>
              <p className="rec-author">by {recommendation.author}</p>
              <div className="rec-meta">
                {recommendation.rating != null && (
                  <span className="rec-rating">
                    ★ {Number(recommendation.rating).toFixed(1)}
                  </span>
                )}
                {recommendation.genre && (
                  <span className="rec-genre">{recommendation.genre}</span>
                )}
                {recommendation.year_published && (
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {recommendation.year_published}
                  </span>
                )}
              </div>
              {recommendation.branch_name && (
                <p className="rec-location">
                  📍 Available at {recommendation.branch_name}
                </p>
              )}
            </div>
          </div>
          <button
            className="recommend-btn secondary"
            onClick={getRecommendation}
            disabled={loading}
          >
            Try Another
          </button>
        </div>
      )}
    </>
  );

  if (inline) return inner;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <button className="dialog-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
        {inner}
      </div>
    </div>
  );
}

export default CouncilRecommendation;
